// CreatorRecordingsPanel.tsx — enregistrements d'un créateur, dans l'onglet Ressources de sa fiche
//
// Même présentation que l'onglet « Enregistrements » du Dashboard (barre de filtres, bascule
// cartes / liste, mêmes cartes, même liste, téléchargement des fichiers et des angles), limitée
// aux prises du créateur. « Lire » ouvre le fichier, « Direct » la chaîne en cours de prise.
// Données : admin-api DashboardRecordings, paramètre ownerId (toutes ses prises, sans période).

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import styles from './ChannelsSection.module.scss';
import tabs   from './DashboardTabs.module.scss';
import extra  from './VideosSection.module.scss';
import panels from './CreatorPanels.module.scss';
import dash   from './DashboardV2.module.scss';
import { ListTable, TitleCell, WhenCell, RowActions, Dash } from './DashboardList';
import { Segment, ViewToggle, useView, plural, size, clock, hour, dayLabel } from './dashboardShared';
import { DashboardRecording, RecState, stateOf, contentText, totalBytes, StateBadge, Thumb, MiniThumb, Actions } from './RecordingsSection';
import VideoPlayer from '../../widget/players/VideoPlayer';
import { API_URLS, WWW_DOMAIN } from '../../../backendServices/urls';

const VIEW_KEY = 'adminResources.recordingsView';

interface Props {
  adminId : number;
}

const CreatorRecordingsPanel: React.FC<Props> = ({ adminId }) => {

  const [ recordings, setRecordings ] = useState<DashboardRecording[]>([]);
  const [ loading   , setLoading    ] = useState(true);
  const [ error     , setError      ] = useState(false);
  const [ state     , setState      ] = useState<'all' | RecState>('all');
  const [ query     , setQuery      ] = useState('');
  const [ view      , setView       ] = useView(VIEW_KEY);
  const [ file      , setFile       ] = useState<DashboardRecording | null>(null);
  const [ live      , setLive       ] = useState<DashboardRecording | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(false);
    axios.get(API_URLS.getDashboardRecordings, { params: { ownerId: adminId, limit: 1000 } }).then(({ data }) => {
      if (cancelled) return;
      setRecordings((data.recordings ?? []).map((x: any): DashboardRecording => ({
        id: x.id, name: x.name || '', format: x.format || 'mp4', channelId: x.channelId ?? 0, channelName: x.channelName || '',
        publicRef: x.publicRef || '', channelOn: !!x.channelOn, owner: x.owner || '', ownerEmail: x.ownerEmail || '',
        durationSeconds: x.durationSeconds ?? null, sizeBytes: x.sizeBytes ?? null, fileUrl: x.fileUrl ?? null,
        creationDate: x.creationDate, stoppedAt: x.stoppedAt ?? null,
        recording: !!x.recording, elapsedSeconds: x.elapsedSeconds ?? null,
        angles: Array.isArray(x.angles) ? x.angles : [], geo: x.geo ?? null,
      })));
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminId]);

  const matching = useMemo(() => {
    const q = query.trim().toLowerCase();
    return recordings.filter(r => !q || `${r.channelName} ${r.name}`.toLowerCase().includes(q));
  }, [recordings, query]);

  const shown = useMemo(() => matching.filter(r => state === 'all' || stateOf(r) === state), [matching, state]);

  const counts = useMemo(() => {
    const c: Record<'all' | RecState, number> = { all: matching.length, live: 0, done: 0, nofile: 0 };
    matching.forEach(r => { c[stateOf(r)]++; });
    return c;
  }, [matching]);

  const actions = (r: DashboardRecording, noRegie = true) =>
    <Actions r={r} onPlayFile={setFile} onWatchLive={setLive} noRegie={noRegie} />;

  if (loading) return <span>Chargement des enregistrements…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (recordings.length === 0) return <div className={styles.empty}>Aucun enregistrement.</div>;

  const facts = (r: DashboardRecording): [string, string][] => [
    [ 'Chaîne', r.channelName ], [ 'Début', `${dayLabel(r.creationDate)} ${hour(r.creationDate)}` ],
    [ 'Durée', clock(r.durationSeconds) ], [ 'Taille', size(r.sizeBytes) ], [ 'Angles', r.angles.length ? String(r.angles.length) : '' ],
  ];

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Chaîne, fichier…" aria-label="Rechercher un enregistrement"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', `Toutes les prises ${counts.all}`], ['live', `En cours ${counts.live}`], ['done', `Terminées ${counts.done}`], ['nofile', `Sans fichier ${counts.nofile}`] ] as const} />
        {(query.trim() !== '' || state !== 'all') && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'prise')}</strong> sur {recordings.length}</span>
          <button type="button" className={extra.reset} onClick={() => { setQuery(''); setState('all'); }}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {shown.length === 0 && <div className={styles.empty}>Aucun enregistrement ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(r => (
            <article key={r.id} className={`${styles.card} ${r.recording ? tabs.cardRec : stateOf(r) === 'nofile' ? tabs.cardWarn : ''}`}>
              <Thumb r={r} elapsed={r.elapsedSeconds} />
              <div className={styles.cardBody}>
                <span className={`${styles.cardName} ${tabs.mono}`} title={`${r.name}.${r.format}`}>{r.name}.{r.format}</span>
                <div className={tabs.cardMeta}>
                  <span>{dayLabel(r.creationDate)} {hour(r.creationDate)} → {r.recording ? <span className={tabs.red}>en cours</span> : (hour(r.stoppedAt) || '?')}</span>
                  {size(totalBytes(r)) && <span>{size(totalBytes(r))}</span>}
                </div>
                <div className={styles.chips}>
                  <span>{r.channelName}</span>
                  <span>{contentText(r)}</span>
                  {stateOf(r) === 'nofile' && <span className={tabs.chipWarn}>Aucun fichier produit</span>}
                </div>
                {actions(r)}
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <ListTable items={shown} rowKey={r => r.id} dateOf={r => r.creationDate}
                   noun={{ one: 'prise', many: 'prises' }} columns={[
          { id: 'take', label: 'Prise', cell: r =>
              <TitleCell thumb={<MiniThumb r={r} />} title={r.channelName} titleHint={`${r.name}.${r.format}`}
                         sub={<><span className={tabs.mono}>{r.name}.{r.format}</span> · {contentText(r)}</>} /> },
          { id: 'state', label: 'État', cell: r => <StateBadge r={r} /> },
          { id: 'duration', label: 'Durée', right: true, sort: (x, y) => (y.durationSeconds ?? -1) - (x.durationSeconds ?? -1),
            cell: r => r.recording ? clock(r.elapsedSeconds) : (clock(r.durationSeconds) || <Dash/>) },
          { id: 'size', label: 'Taille', right: true, sort: (x, y) => totalBytes(y) - totalBytes(x), cell: r => size(totalBytes(r)) || <Dash/> },
          { id: 'date', label: 'Début', cell: r => <WhenCell iso={r.creationDate} /> },
          { id: 'actions', label: '', right: true, cell: r => <div className={panels.always}><RowActions>{actions(r)}</RowActions></div> },
        ]} />
      )}

      {file && file.fileUrl && (
        <div className={dash.overlay} onClick={() => setFile(null)}>
          <div className={dash.modal} onClick={e => e.stopPropagation()}>
            <div className={dash.sectionHeader}>
              <span>{file.name}.{file.format}</span>
              <button className={dash.refresh} onClick={() => setFile(null)} title="Fermer">✕</button>
            </div>
            {facts(file).filter(([, v]) => v).map(([k, v]) => <div key={k} className={dash.zoomInfo}>{k} : {v}</div>)}
            <video className={dash.zoomMedia} src={file.fileUrl} controls autoPlay playsInline />
          </div>
        </div>
      )}

      {live && (
        <div className={dash.overlay} onClick={() => setLive(null)}>
          <div className={dash.modal} onClick={e => e.stopPropagation()}>
            <div className={dash.sectionHeader}>
              <span>Direct — {live.channelName}</span>
              <button className={dash.refresh} onClick={() => setLive(null)} title="Fermer">✕</button>
            </div>
            <VideoPlayer url={`https://${WWW_DOMAIN}/${live.publicRef}/index.m3u8`} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorRecordingsPanel;
